// 🔧 automation_scheduler.js
// ✅ Purpose: Admin-facing Automation Scheduler settings page for LGV Sales Automation
// ✅ Features:
// - Controls whether scheduled sales automation is enabled
// - Configures automation cadence and preferred run hour
// - Configures queue safety limits to prevent runaway AI work
// - Configures per-run processing limits for research, drafts, and follow-ups
// - Stores settings in Firestore `sales_agent_settings/main`
// - Does NOT run agents directly
// - Does NOT send emails or social messages
// - Uses shared SalesPageLayout for consistent LGV styling
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import SalesPageLayout from "../../components/sales/SalesPageLayout";

const defaultSchedulerSettings = {
  automationEnabled: false,

  runEveryHours: 24,
  startHour: 2,
  timezone: "America/Los_Angeles",

  maxResearchPerRun: 10,
  maxDraftsPerRun: 10,
  maxFollowupsPerRun: 15,

  pauseIfReviewQueueAbove: 25,
  pauseIfResearchQueueAbove: 100,
  pauseIfDraftQueueAbove: 50,

  emergencyStop: false,
};

function AutomationScheduler() {
  const [settings, setSettings] = useState(defaultSchedulerSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState(null);

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus(null), 20000);
  };

  const loadSettings = async () => {
    try {
      setLoading(true);

      const settingsRef = doc(db, "sales_agent_settings", "main");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        setSettings({
          ...defaultSchedulerSettings,
          ...settingsSnap.data(),
        });
      } else {
        setSettings(defaultSchedulerSettings);
      }
    } catch (error) {
      console.error("❌ Failed to load Automation Scheduler settings:", error);
      showStatus("error", "Failed to load Automation Scheduler settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      await setDoc(
        doc(db, "sales_agent_settings", "main"),
        {
          ...settings,

          runEveryHours: Number(settings.runEveryHours) || 24,
          startHour: Number(settings.startHour) || 2,

          maxResearchPerRun: Math.min(
            15,
            Math.max(1, Number(settings.maxResearchPerRun) || 10)
          ),
          maxDraftsPerRun: Number(settings.maxDraftsPerRun) || 10,
          maxFollowupsPerRun: Number(settings.maxFollowupsPerRun) || 15,

          pauseIfReviewQueueAbove:
            Number(settings.pauseIfReviewQueueAbove) || 25,
          pauseIfResearchQueueAbove:
            Number(settings.pauseIfResearchQueueAbove) || 100,
          pauseIfDraftQueueAbove:
            Number(settings.pauseIfDraftQueueAbove) || 50,

          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      showStatus("success", "Automation Scheduler settings saved.");
    } catch (error) {
      console.error("❌ Failed to save Automation Scheduler settings:", error);
      showStatus("error", "Failed to save Automation Scheduler settings.");
    } finally {
      setSaving(false);
    }
  };

  const automationStateLabel = settings.emergencyStop
    ? "Emergency Stopped"
    : settings.automationEnabled
      ? "Enabled"
      : "Disabled";

  const automationBadgeClass = settings.emergencyStop
    ? "bg-danger"
    : settings.automationEnabled
      ? "bg-success"
      : "bg-secondary";

  return (
    <SalesPageLayout
      title="Automation Scheduler"
      subtitle="Configure scheduled LGV Sales Automation timing, limits, and safety stops."
      status={status}
      actions={
        <>
          <Link className="btn btn-outline-primary" to="/admin/ai-mission-control">
            AI Mission Control
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/agent-settings">
            Agent Settings
          </Link>
        </>
      }
    >
      {loading ? (
        <div className="alert alert-info">
          Loading Automation Scheduler settings...
        </div>
      ) : (
        <form onSubmit={handleSaveSettings}>
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Automation Status</strong>

              <span className={`badge ${automationBadgeClass}`}>
                {automationStateLabel}
              </span>
            </div>

            <div className="card-body">
              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="automationEnabled"
                  checked={settings.automationEnabled}
                  onChange={(e) =>
                    handleChange("automationEnabled", e.target.checked)
                  }
                  disabled={settings.emergencyStop}
                />

                <label className="form-check-label" htmlFor="automationEnabled">
                  Enable scheduled automation
                </label>

                <div className="small text-muted">
                  Future scheduled functions will check this before running.
                </div>
              </div>

              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="emergencyStop"
                  checked={settings.emergencyStop}
                  onChange={(e) =>
                    handleChange("emergencyStop", e.target.checked)
                  }
                />

                <label className="form-check-label" htmlFor="emergencyStop">
                  Emergency stop
                </label>

                <div className="small text-muted">
                  When enabled, all scheduled automation should immediately stop.
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Schedule</strong>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Run Every Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="168"
                    value={settings.runEveryHours}
                    onChange={(e) =>
                      handleChange("runEveryHours", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Preferred Start Hour</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="23"
                    value={settings.startHour}
                    onChange={(e) =>
                      handleChange("startHour", e.target.value)
                    }
                  />

                  <div className="small text-muted">
                    Uses 24-hour time. Example: 2 means 2:00 AM.
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Timezone</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settings.timezone}
                    onChange={(e) =>
                      handleChange("timezone", e.target.value)
                    }
                  />

                  <div className="small text-muted">
                    Default: America/Los_Angeles.
                  </div>
                </div>
              </div>

              <div className="alert alert-info mb-0">
                This page saves scheduler configuration. The scheduled Cloud
                Function will be added separately and will respect these values.
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Per-Run Work Limits</strong>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Max Research Per Run</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="15"
                    value={settings.maxResearchPerRun}
                    onChange={(e) =>
                      handleChange("maxResearchPerRun", e.target.value)
                    }
                  />
                  <div className="form-text">Maximum 15 to stay within the workflow timeout.</div>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Max Drafts Per Run</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="100"
                    value={settings.maxDraftsPerRun}
                    onChange={(e) =>
                      handleChange("maxDraftsPerRun", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Max Follow-ups Per Run</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="100"
                    value={settings.maxFollowupsPerRun}
                    onChange={(e) =>
                      handleChange("maxFollowupsPerRun", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Queue Protection</strong>
            </div>

            <div className="card-body">
              <p className="text-muted">
                These limits prevent the AI from creating more work than you can
                review. If a queue exceeds its threshold, scheduled automation
                should pause or skip that stage.
              </p>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Pause If Review Queue Above
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="500"
                    value={settings.pauseIfReviewQueueAbove}
                    onChange={(e) =>
                      handleChange("pauseIfReviewQueueAbove", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Pause If Research Queue Above
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="500"
                    value={settings.pauseIfResearchQueueAbove}
                    onChange={(e) =>
                      handleChange("pauseIfResearchQueueAbove", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Pause If Draft Queue Above
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="500"
                    value={settings.pauseIfDraftQueueAbove}
                    onChange={(e) =>
                      handleChange("pauseIfDraftQueueAbove", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving Scheduler..." : "Save Scheduler Settings"}
          </button>
        </form>
      )}
    </SalesPageLayout>
  );
}

export default AutomationScheduler;
